import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault } from 'jotai/utils';
import {
  AuditorDateRangesPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../data-models/preferences-v3.data-model';
import { EntityType } from '../../data-models/field2.data-model';
import { Commentary } from '../../schemas/Commentary.schema';
import { preferenceAtom } from '../../util/preferences-service-v3';
import { getCommentariesForEntity } from '../queries/MaggieCommentaryQueries';
import { PermissionService } from '../PermissionService';

const auditorDateRangesPreferenceAtom = preferenceAtom<AuditorDateRangesPreferences>({
  preferenceKey: PreferenceKey.auditorDateRanges,
  scope: PreferenceScope.ORGANIZATION,
});

export const investmentThesisByCompanyIdAtom = atomFamily((companyId: number) =>
  atom(
    getCommentariesForEntity({
      entity: EntityType.companyInvestmentThesis,
      entityId: companyId,
    })
  )
);

export const commentariesByCompanyIdState = atomFamily((companyId: number) =>
  atomWithDefault<Commentary[] | Promise<Commentary[]>>(async (get) => {
    const investmentThesis = (await get(investmentThesisByCompanyIdAtom(companyId))).at(0);
    const commentaries = await getCommentariesForEntity({
      entity: EntityType.company,
      entityId: companyId,
    });
    return [...commentaries, ...(investmentThesis ? [investmentThesis] : [])];
  })
);

function getCommentaryDate(commentary: Commentary): string | null {
  if (commentary.entity === EntityType.companyInvestmentThesis) {
    return commentary.createdAt ?? null;
  }
  return commentary.date ?? null;
}

export const filteredCommentariesByCompanyIdState = atomFamily((companyId: number) =>
  atom(async (get) => {
    const commentaries = await get(commentariesByCompanyIdState(companyId));

    const isAuditor = PermissionService.get().isAuditor();
    if (!isAuditor) {
      return commentaries;
    }

    const preferences = await get(auditorDateRangesPreferenceAtom);

    if (!preferences?.restrictToRange) {
      return commentaries;
    }

    const startDate = new Date(preferences.startDate);
    const endDate = new Date(preferences.endDate);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return commentaries;
    }

    return commentaries.filter((commentary) => {
      const dateStr = getCommentaryDate(commentary);
      if (!dateStr) {
        return false;
      }
      const commentaryDate = new Date(dateStr);
      return commentaryDate >= startDate && commentaryDate <= endDate;
    });
  })
);

export const commentariesByIdState = atomFamily((companyId: number) =>
  atom(async (get) => {
    const commentaries = await get(commentariesByCompanyIdState(companyId));
    return commentaries.reduce(
      (map, commentary) => map.set(commentary.id, commentary),
      new Map<number, Commentary>()
    );
  })
);
